import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  AirHandlerTypeType,
  ApplianceItemType,
  AttributeFollowUpType,
  AttributeType,
  BTUMultiplierType,
  ChatbotConversationCategoryType,
  ChatbotSubAgentContextOptionType,
  ChatbotSubAgentToolType,
  ChatbotSubAgentType,
  FenestrationItemType,
  ItemAttributeLinkType,
  ItemType,
  OrchestratorModelType,
  OrchestratorProviderType,
  OrchestratorTaskModelType,
  PromptType,
  ScoreDefinitionAdminType,
  ScoringParameterAdminType,
  ScoringScenarioAdminType,
  SettingsType,
  StructureSubTypeRuleAdminType,
  StructureTypeType,
  SystemDesignRuleType,
  SystemSelectorCustomerType,
  TextKeyType,
  ValidItemsType
} from 'data/types';
import axios from 'utils/api';
import {
  setAirHandlerTypes,
  setApplianceItems,
  setAttributeFollowUps,
  setAttributes,
  setBtuMultipliers,
  setChatbotContextOptions,
  setChatbotConversationCategories,
  setChatbotSubAgents,
  setChatbotSubAgentTools,
  setDesignRuleRecommendationTypes,
  setDesignRules,
  setDesignRuleStepOptions,
  setFenestration,
  setInsulationLevelSettings,
  setItems,
  setLoading,
  setMcpToolNames,
  setOrchestratorModels,
  setOrchestratorProviders,
  setOrchestratorTaskModels,
  setPrompts,
  setScoreDefinitions,
  setScoringParameters,
  setScoringScenarios,
  setSettings,
  setStructureSubTypeRules,
  setStructureTypes,
  setSystemSelectorCustomers,
  setTextKeys,
  setValidItems
} from './admin-slice';

export const fetchDefaultAdminData = createAsyncThunk(
  'admin/fetchDefaultData',
  async (_, { dispatch }) => {
    dispatch(setLoading(true));

    try {
      const [
        newItems,
        itemAttributeLinks,
        newAttributes,
        newAttributeFollowUps,
        fetchedStructureTypes,
        fetchedValidItems,
        fetchedAirHandlerTypes,
        fetchedFenestration,
        fetchedApplianceItems,
        fetchedSettings,
        fetchedPrompts,
        fetchedDesignRules,
        fetchedDesignRuleStepOptions,
        fetchedDesignRuleRecommendationTypes,
        fetchedTextKeys,
        fetchedInsulationLevelSettings,
        fetchedOrchestratorProviders,
        fetchedOrchestratorModels,
        fetchedOrchestratorTaskModels,
        fetchedChatbotSubAgents,
        fetchedChatbotSubAgentTools,
        fetchedChatbotConversationCategories,
        fetchedChatbotContextOptions,
        fetchedStructureSubTypeRules,
        fetchedMcpToolNames,
        fetchedBtuMultipliers,
        fetchedScoringScenarios,
        fetchedScoringParameters,
        fetchedScoreDefinitions,
        fetchedSystemSelectorCustomers
      ] = await Promise.all([
        axios.get('items').then((res) => res.data as ItemType[]),
        axios
          .get('item-attribute-links')
          .then((res) => res.data as ItemAttributeLinkType[]),
        axios.get('attributes').then((res) => res.data as AttributeType[]),
        axios
          .get('attribute-followups')
          .then((res) => res.data as AttributeFollowUpType[]),
        axios
          .get('structure-types')
          .then((res) => res.data as StructureTypeType[]),
        axios.get('valid-items').then((res) => res.data as ValidItemsType[]),
        axios
          .get('air-handler-types')
          .then((res) => res.data as AirHandlerTypeType[]),
        axios
          .get('fenestration')
          .then((res) => res.data as FenestrationItemType[]),
        axios.get('appliances').then((res) => res.data as ApplianceItemType[]),
        axios.get('settings').then((res) => res.data as SettingsType),
        axios.get('prompts').then((res) => res.data as PromptType[]),
        axios
          .get('design-rules')
          .then((res) => res.data as SystemDesignRuleType[]),
        axios
          .get('design-rule-step-options')
          .then((res) => res.data as string[]),
        axios
          .get('design-rule-recommendation-types')
          .then(
            (res) => res.data as { id: number; code: string; label: string }[]
          ),
        axios.get('text-keys').then((res) => res.data as TextKeyType[]),
        axios.get('sizing/insulation-level-settings').then((res) => res.data),
        axios
          .get('orchestrator-providers')
          .then((res) => res.data as OrchestratorProviderType[]),
        axios
          .get('orchestrator-models')
          .then((res) => res.data as OrchestratorModelType[]),
        axios
          .get('orchestrator-task-models')
          .then((res) => res.data as OrchestratorTaskModelType[]),
        axios
          .get('chatbot-sub-agents')
          .then((res) => res.data as ChatbotSubAgentType[]),
        axios
          .get('chatbot-sub-agent-tools')
          .then((res) => res.data as ChatbotSubAgentToolType[]),
        axios
          .get('conversation-categories')
          .then((res) => res.data as ChatbotConversationCategoryType[]),
        axios
          .get('chatbot-context-options')
          .then((res) => res.data as ChatbotSubAgentContextOptionType[]),
        axios
          .get('structure-sub-type-rules')
          .then((res) => res.data as StructureSubTypeRuleAdminType[]),
        axios.get('mcp-tool-names').then((res) => res.data as string[]),
        axios
          .get('btu-multipliers')
          .then((res) => res.data as BTUMultiplierType[]),
        axios
          .get('admin/scoring-scenarios')
          .then((res) => res.data as ScoringScenarioAdminType[]),
        axios
          .get('admin/scoring-parameters')
          .then((res) => res.data as ScoringParameterAdminType[]),
        axios
          .get('admin/score-definitions')
          .then((res) => res.data as ScoreDefinitionAdminType[]),
        axios
          .get('admin/customers')
          .then((res) => res.data as SystemSelectorCustomerType[])
      ]);
      const formattedAttributes = newAttributes.map((attr) => {
        const followUps = newAttributeFollowUps.filter(
          (af) => af.followUpAttributeId === attr.id
        );
        return followUps.length === 0
          ? attr
          : {
              ...attr,
              parentAttributes: followUps.map((f) => ({
                attributeId: f.attributeId,
                value: f.value
              }))
            };
      });
      const formattedItems = newItems.map((item) => {
        const links = itemAttributeLinks.filter(
          (link) => link.itemId === item.id
        );
        const itemAttributeIds = links.map((link) => link.attributeId);
        const itemAttributes = formattedAttributes.filter((attr) =>
          itemAttributeIds.includes(attr.id)
        );
        const formattedItemAttributes = itemAttributes.map((attr) => {
          const link = links.find((l) => l.attributeId === attr.id);
          return {
            ...attr,
            parentItemId: item.id,
            value: link ? link.defaultValue : attr.value,
            required: !!link?.required
          };
        });
        return {
          ...item,
          attributes: formattedItemAttributes
        };
      });

      dispatch(setAttributes(formattedAttributes));
      dispatch(setAttributeFollowUps(newAttributeFollowUps));
      dispatch(setItems(formattedItems));
      dispatch(setStructureTypes(fetchedStructureTypes));
      dispatch(setValidItems(fetchedValidItems));
      dispatch(setAirHandlerTypes(fetchedAirHandlerTypes));
      dispatch(setFenestration(fetchedFenestration));
      dispatch(setApplianceItems(fetchedApplianceItems));
      dispatch(setSettings(fetchedSettings));
      dispatch(setPrompts(fetchedPrompts));
      dispatch(setDesignRules(fetchedDesignRules));
      dispatch(setDesignRuleStepOptions(fetchedDesignRuleStepOptions));
      dispatch(
        setDesignRuleRecommendationTypes(fetchedDesignRuleRecommendationTypes)
      );
      dispatch(setTextKeys(fetchedTextKeys));
      dispatch(setInsulationLevelSettings(fetchedInsulationLevelSettings));
      dispatch(setOrchestratorProviders(fetchedOrchestratorProviders));
      dispatch(setOrchestratorModels(fetchedOrchestratorModels));
      dispatch(setOrchestratorTaskModels(fetchedOrchestratorTaskModels));
      dispatch(setChatbotSubAgents(fetchedChatbotSubAgents));
      dispatch(setChatbotSubAgentTools(fetchedChatbotSubAgentTools));
      dispatch(
        setChatbotConversationCategories(fetchedChatbotConversationCategories)
      );
      dispatch(setChatbotContextOptions(fetchedChatbotContextOptions));
      dispatch(setStructureSubTypeRules(fetchedStructureSubTypeRules));
      dispatch(setMcpToolNames(fetchedMcpToolNames));
      dispatch(setBtuMultipliers(fetchedBtuMultipliers));
      dispatch(setScoringScenarios(fetchedScoringScenarios));
      dispatch(setScoringParameters(fetchedScoringParameters));
      dispatch(setScoreDefinitions(fetchedScoreDefinitions));
      dispatch(setSystemSelectorCustomers(fetchedSystemSelectorCustomers));
    } finally {
      dispatch(setLoading(false));
    }
  }
);
